/**
 * AuditLogViewer - Audit Log Viewer with Role-Based Access
 * Migrated from src/app/components/admin/AuditLogViewer.jsx
 * 
 * SuperAdmin: Full access to all audit logs
 * Admin: Only see their own actions
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import DataTable from '../components/common/DataTable';

const { TableSkeleton } = SkeletonLoaderComponents;
import SearchBar from '../components/common/SearchBar';
import { API_ENDPOINTS } from '../config/api-endpoints';

const AuditLogViewer = () => {
  const { user: currentUser } = useAdminAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    action: '',
    dateFrom: '',
    dateTo: ''
  });
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;

  const isSuperAdmin = currentUser?.isSuperAdmin || false;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const isInitialMount = useRef(true);

  // Stable search handler to prevent SearchBar re-triggering
  const handleSearch = useCallback((value, filterValues) => {
    setFilters(prev => ({ ...prev, ...filterValues }));
  }, []);

  const fetchLogs = useCallback(async () => {
    // Prevent infinite retry loop
    if (retryCountRef.current >= MAX_RETRIES) {
      setError('Failed to load audit logs after multiple attempts. Please refresh the page or contact support.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const params = new URLSearchParams();
      const currentFilters = filtersRef.current;
      if (currentFilters.action) params.append('action', currentFilters.action);
      if (currentFilters.dateFrom) params.append('dateFrom', currentFilters.dateFrom);
      if (currentFilters.dateTo) params.append('dateTo', currentFilters.dateTo);
      
      // Non-SuperAdmins only see their own logs
      if (!isSuperAdmin) {
        params.append('adminId', currentUser.userId);
      }

      const response = await fetch(`${API_ENDPOINTS.ADMIN.AUDIT_LOGGER}/logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data?.logs || data?.data?.logs || []);
        retryCountRef.current = 0; // Reset retry count on success
        setError(null);
      } else {
        throw new Error(`Failed to load audit logs: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      console.error('[AuditLogViewer] Error:', err);
      retryCountRef.current += 1;
      
      if (retryCountRef.current >= MAX_RETRIES) {
        setError('Failed to load audit logs after multiple attempts. Please refresh the page or contact support.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, currentUser?.userId]);

  // Fetch on mount only
  useEffect(() => {
    fetchLogs();
  }, []);

  // Re-fetch when filters change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchLogs();
  }, [filters]);

  const columns = [
    {
      key: 'timestamp',
      label: 'Timestamp',
      render: (log) => new Date(log.timestamp).toLocaleString()
    },
    {
      key: 'admin',
      label: 'Admin',
      render: (log) => log.adminId
    },
    {
      key: 'action',
      label: 'Action',
      render: (log) => log.action
    },
    {
      key: 'target',
      label: 'Target',
      render: (log) => `${log.targetType}: ${log.targetId}`
    },
    {
      key: 'details',
      label: 'Details',
      render: (log) => JSON.stringify(log.details || {}).substring(0, 50) + '...'
    }
  ];

  const searchFilters = [
    {
      key: 'action',
      label: 'Action Type',
      options: [
        { value: '', label: 'All Actions' },
        { value: 'USER_UPDATED', label: 'User Updated' },
        { value: 'USER_DISABLED', label: 'User Disabled' },
        { value: 'USER_ENABLED', label: 'User Enabled' },
        { value: 'PASSWORD_RESET', label: 'Password Reset' }
      ]
    }
  ];

  if (loading) {
    return <TableSkeleton rows={8} columns={5} />;
  }

  return (
    <div className="audit-log-viewer">
      <div className="audit-log-viewer__header">
        <h2>Audit Logs</h2>
        {isSuperAdmin ? (
          <span className="audit-log-viewer__super-badge">🔐 SuperAdmin - Full Access</span>
        ) : (
          <span className="audit-log-viewer__admin-badge">👤 Your Actions Only</span>
        )}
      </div>

      {error && (
        <div className="admin-alert admin-alert--warning">
          <span>⚠️</span> {error}
        </div>
      )}

      <div className="admin-card">
        <SearchBar
          placeholder="Search audit logs..."
          onSearch={handleSearch}
          filters={searchFilters}
        />
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Audit Trail ({logs.length})</h3>
        </div>
        <DataTable
          columns={columns}
          data={logs}
          loading={loading}
          emptyMessage="No audit logs found"
        />
      </div>
    </div>
  );
};

export default AuditLogViewer;
